import { extractCreatorId } from './authUtils';
import { orderCountsTowardSoldSales } from './teamSalesOrderEligibility';

const toAmount = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const getItemLineTotal = (item) => {
  const qty = toAmount(item?.quantity) || 1;
  const lineTotal = toAmount(item?.total);
  if (lineTotal > 0) return lineTotal;
  return toAmount(item?.price) * qty;
};

const getProductFromItem = (item) => {
  const p = item?.product || item?.productId;
  return p && typeof p === 'object' ? p : null;
};

export const isCodOrder = (order) => {
  const pm = String(order?.paymentMethod || '').toLowerCase().replace(/_/g, ' ');
  return pm.includes('cod') || pm.includes('cash on delivery');
};

const productDisplayName = (item, product) =>
  item?.name || item?.productName || product?.name || 'Product';

const productKey = (item, product) => {
  const id = product?._id || product?.id || item?.productId;
  if (id && typeof id === 'object' && id.$oid) return id.$oid;
  if (id) return String(id);
  return productDisplayName(item, product);
};

/** Coupon discount applied on order (PKR). */
export function getOrderCouponDiscountAmount(order) {
  const c = order?.coupon || order?.appliedCoupon || {};
  return Math.max(
    0,
    toAmount(
      order?.couponDiscount ??
        order?.pricing?.couponDiscount ??
        c?.discountAmount ??
        c?.amount ??
        order?.discountAmount
    )
  );
}

export function getOrderCouponScopeMeta(order) {
  const c = order?.coupon || order?.appliedCoupon || {};
  const rawScope = c.scope || c.couponScope || '';
  const shopkeeperRef = c.shopkeeperId ?? c.shopkeeper ?? c.createdForShopkeeperId;
  const shopkeeperId = shopkeeperRef ? extractCreatorId(shopkeeperRef) || String(shopkeeperRef) : null;
  const scope =
    rawScope === 'shopkeeper' || (shopkeeperId && rawScope !== 'all')
      ? 'shopkeeper'
      : 'all';
  const perCreator = order?.couponDiscountByCreator;
  const code = c.code || order?.couponCode || '';
  return { scope, shopkeeperId: shopkeeperId ? String(shopkeeperId) : null, perCreator, code };
}

function orderLineTotalsForMatcher(order, creatorMatcher) {
  const items = Array.isArray(order?.items) ? order.items : [];
  let mine = 0;
  let all = 0;
  for (const item of items) {
    const product = getProductFromItem(item);
    const line = getItemLineTotal(item);
    all += line;
    const creatorId = product ? extractCreatorId(product.createdBy) : null;
    if (creatorMatcher(creatorId)) mine += line;
  }
  return { mine, all };
}

/**
 * How much of this order's coupon discount counts toward one creator's sales report.
 */
export function allocateOrderCouponToCreator(order, creatorId, creatorMatcher) {
  const id = String(creatorId || '').trim();
  if (!id) return 0;
  const discount = getOrderCouponDiscountAmount(order);
  if (discount <= 0) return 0;

  const meta = getOrderCouponScopeMeta(order);
  if (meta.perCreator && typeof meta.perCreator === 'object') {
    if (meta.perCreator[id] != null) return toAmount(meta.perCreator[id]);
    if (meta.perCreator.get && typeof meta.perCreator.get === 'function') {
      const v = meta.perCreator.get(id);
      if (v != null) return toAmount(v);
    }
  }

  if (meta.scope === 'shopkeeper' && meta.shopkeeperId) {
    return meta.shopkeeperId === id ? discount : 0;
  }

  const { mine, all } = orderLineTotalsForMatcher(order, creatorMatcher);
  if (mine <= 0 || all <= 0) return 0;
  return (discount * mine) / all;
}

/**
 * @param {object[]} orders
 * @param {(creatorId: string | null) => boolean} creatorMatcher - true = include line
 * @param {{ creatorId?: string }} [options] - when set, attributes coupon discount to this creator
 */
export function buildSalesReport(orders, creatorMatcher, options = {}) {
  const attributionCreatorId = options.creatorId ? String(options.creatorId).trim() : '';
  const productMap = new Map();
  const paymentRows = [];
  let grossLines = 0;
  let codLines = 0;
  let nonCodLines = 0;
  let unitsSold = 0;
  let couponDiscountTotal = 0;
  const ordersTouched = new Set();

  for (const order of orders || []) {
    if (!order || !orderCountsTowardSoldSales(order)) continue;
    const items = Array.isArray(order.items) ? order.items : [];
    const cod = isCodOrder(order);
    let orderMyShare = 0;
    let hadMine = false;

    for (const item of items) {
      const product = getProductFromItem(item);
      const creatorId = product ? extractCreatorId(product.createdBy) : null;
      if (!creatorMatcher(creatorId)) continue;

      const line = getItemLineTotal(item);
      const qty = Math.max(1, Math.round(toAmount(item?.quantity) || 1));
      const key = productKey(item, product);
      const name = productDisplayName(item, product);

      const cur = productMap.get(key) || {
        key,
        name,
        quantity: 0,
        revenue: 0,
        codRevenue: 0,
        nonCodRevenue: 0,
      };
      cur.quantity += qty;
      cur.revenue += line;
      if (cod) cur.codRevenue += line;
      else cur.nonCodRevenue += line;
      productMap.set(key, cur);

      grossLines += line;
      if (cod) codLines += line;
      else nonCodLines += line;
      unitsSold += qty;
      orderMyShare += line;
      hadMine = true;
    }

    if (hadMine) {
      const oid = order._id || order.id;
      if (oid) ordersTouched.add(String(oid));
      const couponMeta = getOrderCouponScopeMeta(order);
      const couponShare = attributionCreatorId
        ? allocateOrderCouponToCreator(order, attributionCreatorId, creatorMatcher)
        : 0;
      couponDiscountTotal += couponShare;
      paymentRows.push({
        orderId: oid,
        orderNumber: order.orderNumber || oid || '—',
        createdAt: order.createdAt || order.date,
        paymentMethod: order.paymentMethod || '—',
        cod,
        myShare: orderMyShare,
        orderTotal: toAmount(order.total),
        couponShare,
        couponCode: couponMeta.code || '',
      });
    }
  }

  paymentRows.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  return {
    products: Array.from(productMap.values()).sort((a, b) => b.revenue - a.revenue),
    payments: paymentRows,
    grossLines,
    codLines,
    nonCodLines,
    unitsSold,
    orderCount: ordersTouched.size,
    couponDiscountTotal,
    netLineRevenue: Math.max(0, grossLines - couponDiscountTotal),
  };
}

export function formatSalesMoney(n) {
  return `Rs. ${Math.round(n).toLocaleString('en-PK')}`;
}

function getOrderDate(order) {
  const raw = order?.createdAt ?? order?.date;
  if (!raw) return null;
  const t = new Date(raw);
  return Number.isNaN(t.getTime()) ? null : t;
}

function startOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function endOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/**
 * @param {'all'|'today'|'7d'|'30d'|'custom'} preset
 */
export function filterOrdersByDate(orders, preset, customFrom = '', customTo = '') {
  if (preset === 'all') return orders;
  if (preset === 'custom' && !customFrom && !customTo) return orders;

  const now = new Date();
  let rangeStart = null;
  let rangeEnd = endOfLocalDay(now);

  if (preset === 'today') {
    rangeStart = startOfLocalDay(now);
  } else if (preset === '7d') {
    const s = new Date(now);
    s.setDate(s.getDate() - 6);
    rangeStart = startOfLocalDay(s);
  } else if (preset === '30d') {
    const s = new Date(now);
    s.setDate(s.getDate() - 29);
    rangeStart = startOfLocalDay(s);
  } else if (preset === 'month') {
    rangeStart = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (preset === 'custom') {
    if (customFrom) {
      rangeStart = startOfLocalDay(new Date(`${customFrom}T12:00:00`));
    } else {
      rangeStart = new Date(0);
    }
    if (customTo) {
      rangeEnd = endOfLocalDay(new Date(`${customTo}T12:00:00`));
    } else {
      rangeEnd = endOfLocalDay(now);
    }
    if (rangeStart > rangeEnd) {
      const a = rangeStart;
      rangeStart = startOfLocalDay(rangeEnd);
      rangeEnd = endOfLocalDay(a);
    }
  }

  return orders.filter((o) => {
    const od = getOrderDate(o);
    if (!od) return false;
    if (rangeStart && od < rangeStart) return false;
    if (rangeEnd && od > rangeEnd) return false;
    return true;
  });
}

/** Matcher for admin: all sold line items. */
export function allCreatorsMatcher() {
  return () => true;
}

/** Matcher for team member: only their products. */
export function teamMemberCreatorMatcher(myId) {
  const id = String(myId || '');
  return (creatorId) => creatorId && String(creatorId) === id;
}

/** Sold line-item revenue for one creator (same rules as Team Sales). */
export function computeCreatorRevenueTotals(orders, creatorId) {
  const id = String(creatorId || '').trim();
  if (!id) {
    return { total: 0, today: 0, month: 0, todayOrders: 0, monthOrders: 0 };
  }
  const eligible = (orders || []).filter(orderCountsTowardSoldSales);
  const report = buildSalesReport(eligible, teamMemberCreatorMatcher(id), { creatorId: id });
  const now = new Date();
  const todayStart = startOfLocalDay(now);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  let today = 0;
  let month = 0;
  const todayOrders = new Set();
  const monthOrders = new Set();

  for (const p of report.payments) {
    const d = p.createdAt ? new Date(p.createdAt) : null;
    if (!d || Number.isNaN(d.getTime())) continue;
    const share = toAmount(p.myShare);
    if (d >= todayStart) {
      today += share;
      if (p.orderId) todayOrders.add(String(p.orderId));
    }
    if (d >= monthStart) {
      month += share;
      if (p.orderId) monthOrders.add(String(p.orderId));
    }
  }

  return {
    total: report.grossLines,
    today,
    month,
    todayOrders: todayOrders.size,
    monthOrders: monthOrders.size,
  };
}
